// The single client store (design D11): three slices — session, connection, world —
// held in one Zustand store so chrome (React selectors) and the PixiJS render loop
// (an imperative `getState()` read on every frame) never hold divergent copies. A
// store update notifies subscribed selectors; an imperative read outside React sees
// the same value without forcing a re-render of components that didn't select it —
// this is plain Zustand selector behaviour, exercised in `App.test.tsx` (task 5.1).

import { create } from 'zustand';
import type { LobbyState } from '../net/lobby-http';
import { ConnState } from '../net/wire/connection';
import type { ConnectionEpoch, WorldSnapshot } from '../net/wire/connection';
import { LIVE_PLAY_RENDER_INPUT, type RenderInput } from '../canvas/render-input';
import type {
  ColonyMeta,
  ManifestSummary,
  MatchResult,
  MatchState,
  PromptStatus,
  RejectReason,
  ScoreboardRow,
  StateHistogram,
  Tier,
} from '../net/wire/messages';

/** Session and lobby data shared by the React shell and network clients. */
export interface SessionSlice {
  sessionToken: string | null;
  matchId: string | null;
  colonyId: number | null;
  role: 'player' | 'spectator' | null;
  lobby: LobbyState | null;
  /** The own colony's static hive world point — the camera anchor for the colony play view
   * (`colony-field-canvas`). Populated only from a decoded `Welcome.yourHive` (via
   * `bindColonyMessages`) and cleared by `reset()`; `null` for a spectator and until the
   * first `Welcome` lands. Session-scoped, re-delivered by the reconnect's `Welcome`, never
   * persisted for reload — exactly as `colonyId` is. */
  ownHive: [number, number] | null;
}

/** Connection-machine state exposed to UI and rendering consumers. */
export interface ConnectionSlice {
  connectionState: ConnState;
  connectionEpoch: ConnectionEpoch;
  confirmedTier: Tier | null;
  lastSeq: bigint | null;
  truncated: boolean;
  /** An explicit, monotonic stream-discontinuity counter (design D9/F7): bumped once
   * per reconnect (a new `connectionEpoch`) AND once per sequence-gap (`GapWait`) entry,
   * never on recovery — maintained additively over the connection setters rather than
   * derived from the transient, non-monotonic gap-recovery state. It rides the
   * render-input context and drives the group-10 per-entity sample-history reset (on
   * either a reconnect or a gap); `connectionEpoch` drives the facing-tombstone reset
   * (a reconnect only), so the two resets can be triggered from distinct signals. */
  streamEpoch: number;
}

/** The latest confirmed observation model and its interpolation predecessor. */
export interface WorldSlice {
  previousWorld: WorldSnapshot | null;
  currentWorld: WorldSnapshot | null;
}

// --- colony slice (colony-screen design D2/D12; client-colony "Colony store slice
// and inbound message routing") -------------------------------------------------

export type ColonyActiveTab = 'flowchart' | 'code';

/** One version's accreted provenance (design D6): the tick its `accepted{v}` was
 * observed, and how many `repairing` statuses were observed in that generation
 * cycle before it ("auto-repaired ×N"). Stored in `provenanceLog`, keyed alongside
 * its canonical text in `versionCache` by `versionCacheKey`. */
export interface VersionProvenance {
  acceptedTick: number;
  repairCount: number;
}

/** The colony-screen UI selection (selected type, active tab, viewed version) —
 * local UI state, but still store-held so no component holds a divergent copy
 * (UI.md §9). `viewedVersion: null` means "viewing the live version". */
export interface ColonySelection {
  selectedTypeId: number | null;
  activeTab: ColonyActiveTab;
  viewedVersion: number | null;
}

/** Composes the `versionCache`/`provenanceLog` key for a version. Version numbers
 * are per-type sequences (DESIGN §5 hot-swap versioning) — a bare version number
 * would collide across types sharing the same number, so every accretion is keyed
 * by (typeId, version), never by version alone. */
export function versionCacheKey(typeId: number, version: number): string {
  return `${typeId}:${version}`;
}

/** Colony metadata, per-type prompt status, own-colony state histogram, the
 * latest match state, the session version cache/provenance log, and the
 * colony-screen UI selection (client-app "The single store carries the colony
 * slice"; client-colony "Colony store slice and inbound message routing").
 * Mutated only via `bindColonyMessages` (a decoded server message) or a local UI
 * selection action — the UI invents no data. */
export interface ColonySlice {
  colonyMeta: ColonyMeta | null;
  promptStatusByType: Record<number, PromptStatus>;
  histogram: StateHistogram | null;
  matchState: MatchState | null;
  /** `versionCacheKey(typeId, version)` -> canonical text, accreted from each
   * `ColonyMeta` as a version goes live (design D6). */
  versionCache: Record<string, string>;
  /** `versionCacheKey(typeId, version)` -> observed provenance, accreted from the
   * `PromptStatus` stream (design D6). */
  provenanceLog: Record<string, VersionProvenance>;
  colonySelection: ColonySelection;
  /** The most recent mutation `Rejected{unauthorized|rateLimited|stale|invalid}`
   * observed this session, or `null`. The wire carries no per-mutation
   * correlation id (design D8), so this is a best-effort "most recent
   * rejection" surface — the Colony screen shows it against the currently-open
   * type sheet's `PromptBox`, not attributed to a specific dispatch.
   * `Rejected{upgrade}` never reaches here — `Connection` handles that
   * terminally itself and never invokes `onMutationRejected` for it. */
  mutationRejection: RejectReason | null;
}

/** Internal bookkeeping only `applyPromptStatus` reads/writes: `repairing`
 * statuses observed for a type in its current generation cycle, feeding the next
 * `accepted`'s `provenanceLog` "auto-repaired ×N" (design D6). Not part of the
 * spec-facing slice surface. */
interface ColonyInternalSlice {
  _repairCountsByType: Record<number, number>;
}

// --- match/summary slice (match-screen design D-sum-1/D-sum-2/D-sum-4; client-app
// "The store carries the match result and the manifest" / "accumulates a bounded
// population timeline with a completeness contract" / "accretes an observed
// own-colony prompt-cycle count") -----------------------------------------------

/** One accreted `matchTimeline` sample: an observed tick's per-colony scoreboard
 * (design D-sum-1). There is no client fetch path for `MatchResult.timelineRef`
 * (the authoritative flat-file payload, D-077 deferred), so this is accrued
 * client-side from observed `MatchState.scoreboard` samples — a *sample* of the
 * match, not a guaranteed whole-match record (see `MatchTimeline.complete`). */
export interface TimelineSample {
  tick: number;
  scoreboard: ScoreboardRow[];
}

/** The bounded population timeline + its completeness contract (design D-sum-1).
 * `complete` is true only when accretion began at match tick 0 (no gap before
 * the first observed sample) — a mid-match join/reconnect (a nonzero
 * `startTick`) is `partial` for the timeline's whole life. Downstream consumers
 * (`client-summary` peak-bees/Best-rewrite) must suppress whole-match claims
 * over a `partial` timeline — the fabrication contract (F3) forbids an
 * authoritative-looking undercount. */
export interface MatchTimeline {
  samples: TimelineSample[];
  /** The first accreted sample's tick since the last `reset()`, or `null`
   * before any sample has landed. Set once and never revised. */
  startTick: number | null;
  complete: boolean;
  /** The connection epoch of the most recently accreted sample, or `null`
   * before any sample has landed (design DD11 / D-080). Compared against the
   * *incoming* sample's epoch on the next accretion to detect a reconnect
   * discontinuity. */
  lastEpoch: ConnectionEpoch | null;
}

/** Caps `matchTimeline.samples` (design D-sum-1 "unbounded timeline memory over
 * a 30-min match" risk). Chosen generously above the six-sample test fixture
 * while still bounding a 30-min match's worth of observations. */
const MATCH_TIMELINE_MAX_SAMPLES = 48;

function initialMatchTimeline(): MatchTimeline {
  return { samples: [], startTick: null, complete: false, lastEpoch: null };
}

/** Whether `sample` records, for at least one colony it carries, that colony's
 * observed min or max `livingCount` across the whole `samples` buffer (first
 * occurrence wins ties). Extrema-preserving compaction never evicts a sample
 * that would erase a colony's peak or trough. */
function isExtremaSample(sample: TimelineSample, samples: readonly TimelineSample[]): boolean {
  for (const row of sample.scoreboard) {
    let min = Infinity;
    let max = -Infinity;
    for (const s of samples) {
      const match = s.scoreboard.find((r) => r.colonyId === row.colonyId);
      if (match !== undefined) {
        if (match.livingCount < min) min = match.livingCount;
        if (match.livingCount > max) max = match.livingCount;
      }
    }
    if (row.livingCount === min || row.livingCount === max) {
      return true;
    }
  }
  return false;
}

/** Whether every colony-extremum `sample` records (per `isExtremaSample`) is a
 * *duplicate* — some OTHER sample in `samples` ties the exact same value for
 * that colony — so evicting `sample` still leaves the extremum recorded
 * elsewhere. A sample that is the SOLE holder of any colony's min or max is
 * never redundant (D-081: a fallback eviction must not erase a genuine,
 * unduplicated extremum just because *some other* colony's tie also flagged
 * the sample as nominally "extremum"). */
function isRedundantExtremum(sample: TimelineSample, samples: readonly TimelineSample[]): boolean {
  for (const row of sample.scoreboard) {
    let min = Infinity;
    let max = -Infinity;
    for (const s of samples) {
      const match = s.scoreboard.find((r) => r.colonyId === row.colonyId);
      if (match !== undefined) {
        if (match.livingCount < min) min = match.livingCount;
        if (match.livingCount > max) max = match.livingCount;
      }
    }
    if (row.livingCount !== min && row.livingCount !== max) {
      continue; // this row isn't why `sample` was flagged extremum; no constraint from it
    }
    const hasDuplicate = samples.some(
      (s) =>
        s !== sample &&
        s.scoreboard.some((r) => r.colonyId === row.colonyId && r.livingCount === row.livingCount),
    );
    if (!hasDuplicate) {
      return false; // sole holder of this colony's extremum -- never safe to evict
    }
  }
  return true;
}

/** Compacts `samples` back under `MATCH_TIMELINE_MAX_SAMPLES` by evicting
 * non-extrema interior samples first (never the first — the match-start
 * anchor — or the last — the latest observation; design D-sum-1: "a capped
 * buffer that retains per-colony min/max, never naive downsampling that could
 * erase a short-lived peak"). A second pass then evicts REDUNDANT extrema —
 * ties that duplicate a value already recorded by some other surviving sample
 * — down to the cap. If every remaining interior sample is uniquely necessary
 * (no duplicate to spare, e.g. one colony's population happens to be flat for
 * the whole series, trivially tying every sample to its min/max), compaction
 * stops there rather than erasing a genuine extremum: the buffer is left
 * above the nominal cap, bounded in practice by at most two surviving samples
 * per distinct colony observed (D-081 — replacing the old unconditional
 * "drop the oldest interior sample anyway" fallback, which could and did
 * evict a genuine, unduplicated extremum). */
function compactTimeline(samples: TimelineSample[]): TimelineSample[] {
  if (samples.length <= MATCH_TIMELINE_MAX_SAMPLES) {
    return samples;
  }
  const next = [...samples];
  for (let i = 1; i < next.length - 1 && next.length > MATCH_TIMELINE_MAX_SAMPLES; i++) {
    if (!isExtremaSample(next[i], next)) {
      next.splice(i, 1);
      i -= 1;
    }
  }
  for (let i = 1; i < next.length - 1 && next.length > MATCH_TIMELINE_MAX_SAMPLES; i++) {
    if (isRedundantExtremum(next[i], next)) {
      next.splice(i, 1);
      i -= 1;
    }
  }
  return next;
}

/** Accretes one `MatchState` observation into `timeline` (design D-sum-1): one
 * sample per observed tick (a repeat of an already-seen tick replaces its entry
 * rather than duplicating it), extrema-preserving compaction once the buffer
 * exceeds the cap, and `startTick`/`complete` fixed by the *first* accreted
 * sample since the last `reset()`. `connectionEpoch` is the store's current
 * `connectionEpoch` at accretion time (design DD11 / D-080): once the timeline
 * has a non-null `lastEpoch`, a sample accreting under a *different* epoch is a
 * reconnect discontinuity that **latches** `complete` to `false` — carrying the
 * prior result forward (`complete = isFirstSample ? tick === 0 : (prevComplete
 * && !discontinuity)`) so a later same-epoch sample never re-completes the
 * timeline over the hole. Exported for the pure-model tests (design.md
 * "matchTimeline accumulation") as well as `applyMatchState`'s own use. */
export function accreteMatchTimeline(
  timeline: MatchTimeline,
  matchState: MatchState,
  connectionEpoch: ConnectionEpoch,
): MatchTimeline {
  const sample: TimelineSample = { tick: matchState.tick, scoreboard: matchState.state.scoreboard };
  const withoutDuplicate = timeline.samples.filter((s) => s.tick !== sample.tick);
  const samples = compactTimeline([...withoutDuplicate, sample].sort((a, b) => a.tick - b.tick));
  const isFirstSample = timeline.startTick === null;
  const startTick = timeline.startTick ?? matchState.tick;
  const discontinuity = timeline.lastEpoch !== null && connectionEpoch !== timeline.lastEpoch;
  const complete = isFirstSample ? matchState.tick === 0 : timeline.complete && !discontinuity;
  return { samples, startTick, complete, lastEpoch: connectionEpoch };
}

/** The match result, manifest, population timeline, and prompt-cycle count the
 * Match/Summary screens read. Mutated only through store actions whose callers
 * trace to a decoded server message (`bindColonyMessages`) — the UI invents no
 * data. */
export interface MatchSummarySlice {
  /** The latest decoded `MatchResult` — the Summary screen's source. `null`
   * until a `result` message has been observed this session. */
  matchResult: MatchResult | null;
  /** The `Welcome.manifest` summary, persisted at handshake (design D-sum-4):
   * `tickRateHz`/`fieldSize` source blob-id unpacking and tick->wall-time
   * conversion; `colonyIds` is the leaderboard's total colony count `M`. */
  manifest: ManifestSummary | null;
  /** The accumulated population timeline + its completeness contract
   * (design D-sum-1). */
  matchTimeline: MatchTimeline;
  /** The number of own-colony `generating` `PromptStatus` observations this
   * session (design D-sum-2) — the Summary "prompts sent" tile's source. Begins
   * at connect with no pre-connect history, so it carries the same completeness
   * limitation as `matchTimeline` and must not be presented as authoritative
   * when the timeline is `partial` (`client-summary`). */
  promptCycleCount: number;
}

// --- replay slice (replay-viewer design "replay is a MODE of the Match screen";
// client-replay "Replay playback state lives in an additive store slice") ---------

/** The replay-playback slice (replay-viewer tasks 7.2): an **additive** slice that
 * reshapes no existing slice (the Stage-4 lane rule that store additions are
 * additive-only). Populated by the authenticated open-replay flow (`enterReplay`)
 * and the read-only playback position; the live/match/colony slices are untouched.
 *
 * `replayResult` is the archived match's persisted `MatchResult`, captured from the
 * open-replay HTTP response (wave-4 F11) and held here until playback reaches the
 * terminal tick, at which point it is applied to `matchResult` to drive the existing
 * `matchResult`→Summary transition (tasks 7.5/7.6). It rides the open-replay response,
 * never the observation stream — no `ServerMessage::Result` is decoded off the replay
 * connection. */
export interface ReplaySlice {
  /** The open replay session token, or `null` when not replaying. A non-null value
   * is the "replay mode" signal the Match screen reads (client-match, group 8). */
  replaySessionId: string | null;
  /** Whether playback is running. A fresh open holds paused at tick 0 (design D1). */
  replayPlaying: boolean;
  /** The requested speed multiplier (the server clamps to `replay_speed_max`). */
  replaySpeed: number;
  /** The read-only playback position (tick). There is no seek field — the position
   * strip is read-only and arbitrary seek is deferred (D-123). */
  replayPosition: number;
  /** The open-replay response's persisted `MatchResult`, applied to `matchResult`
   * at the terminal tick (tasks 7.5/7.6); `null` when not replaying. */
  replayResult: MatchResult | null;
}

// --- onboarding slice (onboarding design D-Slice; client-onboarding "Additive
// onboarding store slice with separate durable and ephemeral lifecycles") ---------

/** First-run guidance state (onboarding design D-Slice), carrying two
 * **separately-lifecycled** concerns so the additive-only `reset()` contract stays
 * unchanged. **Shared by both first-run guides** (WAG-11 added the rounds Sandbox
 * screen's alongside the live Colony screen's): safe because `GameMode` is resolved
 * once at boot and only one of the two screens ever mounts in a given browser tab
 * (`docs/DESIGN.md` §2), so the field is always "has THIS mode's guide been
 * acknowledged" — each screen re-seeds it from its OWN keyed `localStorage` record
 * (`ONBOARDING_SEEN_KEY` / `ROUNDS_ONBOARDING_SEEN_KEY`) on its own mount.
 *
 * - `hasSeenColonyIntro` is a **durable mirror**, never the source of truth: the
 *   versioned `waggle-onboarding-seen` `localStorage` record (`onboarding-store.ts`)
 *   owns acknowledgement. The mirror is seeded from `hasSeenGuide()` at boot
 *   (`main.tsx`) and re-seeded on new-match entry (the Colony/FirstRunGuide gate), so
 *   the first-run gate always traces "seen" to the durable value and a transient
 *   post-`reset()` `false` mirror never wrongly re-shows the guide. `initialState`
 *   value `false`.
 * - `guideStep` / `guideDismissed` are **ephemeral** per-mount progress, cleared
 *   coherently by `reset()`. Because they are store-held (surviving a component
 *   unmount), the non-persisting `resetGuideProgress()` action returns them to their
 *   initial values on Colony route-exit/unmount so a same-match remount restarts the
 *   guide at step one rather than resuming a stale mid-sequence step — WITHOUT
 *   touching `localStorage` (only an explicit dismiss persists). */
export interface OnboardingSlice {
  /** Durable mirror of the `waggle-onboarding-seen` `localStorage` record (design
   * D-Slice) — `true` iff the browser is acknowledged for the current
   * `GUIDE_VERSION`. Seeded from `hasSeenGuide()` on boot and re-seeded on new-match
   * entry; `false` in `initialState`. Never the source of truth (that is the durable
   * record), so a transient `reset()`-induced `false` is harmless — the gate re-seeds. */
  hasSeenColonyIntro: boolean;
  /** The current guide step index (ephemeral, `0` in `initialState`). Store-held so
   * it survives a component unmount; cleared by `reset()` and by the non-persisting
   * `resetGuideProgress()`. */
  guideStep: number;
  /** Per-mount dismiss state (ephemeral, `false` in `initialState`). Cleared by
   * `reset()` and by `resetGuideProgress()`; distinct from the durable
   * `hasSeenColonyIntro` mirror, which is what actually suppresses the guide across
   * matches. */
  guideDismissed: boolean;
  /** The player explicitly asked to re-read the guide, via the help control (WAG-109).
   * Ephemeral, `false` in `initialState`, cleared by `reset()` and by
   * `resetGuideProgress()`.
   *
   * **A separate field rather than clearing `hasSeenColonyIntro`**, for two reasons that
   * are easy to rediscover the hard way. The mirror is re-seeded from the keyed
   * `localStorage` record in a mount-time `useLayoutEffect` on both screens, so a flip
   * would be overwritten by the next mount — the guide would close on its own. And the
   * mirror answers "has this browser acknowledged the guide"; re-reading the help does
   * not un-acknowledge it, so a flip would also make the field untrue while it held.
   * The gate ORs the two: unacknowledged (first run) OR asked for (help). */
  guideRequested: boolean;
}

/** Compile-time shape for the single Zustand store. */
export type ClientStoreState = SessionSlice &
  ConnectionSlice &
  WorldSlice &
  ColonySlice &
  ColonyInternalSlice &
  MatchSummarySlice &
  ReplaySlice &
  OnboardingSlice;

/** Mutators. Every field is updated only through one of these — the UI invents no
 * data (UI.md §9): each setter's caller traces to a decoded message, a lobby-http
 * response, or a local lifecycle event (never fabricated). */
export interface ClientStoreActions {
  setSession(session: Partial<SessionSlice>): void;
  setLobby(lobby: LobbyState | null): void;
  setConnectionState(state: ConnState): void;
  setConnectionEpoch(epoch: ConnectionEpoch): void;
  setConfirmedTier(tier: Tier | null): void;
  setLastSeq(seq: bigint | null): void;
  setTruncated(truncated: boolean): void;
  /** Applies a newly decoded world (keyframe or delta apply): the previous
   * `currentWorld` slides into `previousWorld` so `canvas/interpolate.ts` has its two
   * bracketing tick samples. */
  setWorld(world: WorldSnapshot): void;
  /** Publishes a decoded world **and** its sequence in one atomic update (the live
   * `onWorld` path), so the render-loop subscription sees `currentWorld` and `lastSeq`
   * advance together and redraws exactly once per world update — never a double-draw
   * from two separate writes, and never a reconnect keyframe suppressed because its
   * `lastSeq` was published in a separate write (implementation-review F1). */
  setWorldFrame(world: WorldSnapshot): void;
  /** Resets every slice to its initial value (e.g. on a fresh lobby re-entry). */
  reset(): void;

  // --- colony slice actions (invoked from `bindColonyMessages`, never directly by
  // UI components — the UI invents no data) --------------------------------------

  /** Replaces `colonyMeta` with the latest decoded value and accretes
   * `versionCache` from each type's now-active version's canonical text. */
  applyColonyMeta(meta: ColonyMeta): void;
  /** Records the latest `PromptStatus` for its type and accretes `provenanceLog`
   * on `accepted` (design D6: prompted-ago tick + the repair count observed since
   * the preceding `generating`). */
  applyPromptStatus(status: PromptStatus): void;
  /** Replaces `histogram` with the latest decoded own-colony `StateHistogram`. */
  applyStateHistogram(histogram: StateHistogram): void;
  /** Replaces `matchState` with the latest decoded `MatchState`. */
  applyMatchState(matchState: MatchState): void;
  /** Records the latest mutation `Rejected{other}` (never `upgrade` — see
   * `mutationRejection`'s doc). Invoked from `bindColonyRejection`, wired as the
   * shared connection's `onMutationRejected` callback. */
  applyMutationRejected(reason: RejectReason): void;
  /** Clears the surfaced rejection, e.g. once the player dismisses it or takes
   * a fresh action. */
  dismissMutationRejection(): void;

  // --- colony-screen UI selection (local, not server-sourced) --------------------

  selectColonyType(typeId: number | null): void;
  setColonyActiveTab(tab: ColonyActiveTab): void;
  setViewedVersion(version: number | null): void;

  // --- match/summary slice actions (invoked from `bindColonyMessages`, never
  // directly by UI components — the UI invents no data) -------------------------

  /** Replaces `matchResult` with the latest decoded `MatchResult`. */
  applyMatchResult(result: MatchResult): void;
  /** Persists the `Welcome.manifest` summary (design D-sum-4). */
  setManifest(manifest: ManifestSummary): void;

  // --- replay slice actions (replay-viewer group 7; invoked from the open-replay
  // flow and the group-8 Match-screen replay wiring, never fabricated) -----------

  /** Enters replay mode from a successful open-replay: records the replay session
   * token and the response's persisted `MatchResult`, opening **paused at tick 0**
   * at 1× (design D1 — a new driver opens paused and advances only on the first
   * play). Reshapes no existing slice; in particular it does NOT set `matchResult`
   * (that happens only at the terminal tick, `setReplayPosition`). */
  enterReplay(open: { replaySessionId: string; result: MatchResult }): void;
  /** Advances the read-only playback position. Once the position reaches the
   * captured `replayResult.tick`, applies that `MatchResult` to `matchResult`
   * exactly once (tasks 7.5/7.6) — driving the existing `matchResult`→Summary
   * transition from the open-replay response, not a streamed `ServerMessage::Result`. */
  setReplayPosition(position: number): void;
  /** Leaves replay mode, clearing the whole replay slice back to inert. */
  exitReplay(): void;

  // --- onboarding slice actions (onboarding design D-Slice) --------------------

  /** Seeds/re-seeds the durable `hasSeenColonyIntro` mirror from the
   * `waggle-onboarding-seen` `localStorage` record (via `hasSeenGuide()`): called at
   * boot (`main.tsx`), on new-match entry (the Colony gate), and set `true` on an
   * explicit dismiss. Purely mirrors the durable authority — the record, not this
   * field, is the source of truth. */
  setHasSeenColonyIntro(seen: boolean): void;
  /** Advances/sets the ephemeral guide step (design D-Slice): the `FirstRunGuide`
   * Next/Back controls drive it through the store rather than component-local state,
   * so the step is store-held and survives a component unmount — which is precisely
   * why `resetGuideProgress()` must clear it on route-exit. Purely ephemeral: never
   * touches `localStorage`, cleared by `reset()`. */
  setGuideStep(step: number): void;
  /** Returns the ephemeral guide progress (`guideStep`, `guideDismissed`,
   * `guideRequested`) to its initial values WITHOUT any `localStorage` write — the
   * Colony/FirstRunGuide route-exit/unmount effect fires it so a same-match remount
   * restarts the guide at step one. Distinct from `reset()` (which stays the blanket
   * `set(initialState)`); this is a targeted, non-persisting ephemeral-progress clear.
   * Clearing `guideRequested` here is what stops a route-exit leaving the guide armed
   * to re-open on the next mount (WAG-109). */
  resetGuideProgress(): void;
  /** The player asked to re-read the guide (the help control, WAG-109): arms
   * `guideRequested` and rewinds progress so it opens at step one rather than resuming
   * mid-sequence. Writes no `localStorage` and leaves `hasSeenColonyIntro` alone —
   * re-reading the help is not un-acknowledging the guide. Dismissing clears the arm
   * (the screens' dismiss handler), so the control is repeatable. */
  requestGuide(): void;
}

export type ClientStore = ClientStoreState & ClientStoreActions;

const initialState: ClientStoreState = {
  sessionToken: null,
  matchId: null,
  colonyId: null,
  role: null,
  lobby: null,
  ownHive: null,
  connectionState: ConnState.Idle,
  connectionEpoch: 0,
  confirmedTier: null,
  lastSeq: null,
  truncated: false,
  streamEpoch: 0,
  previousWorld: null,
  currentWorld: null,
  colonyMeta: null,
  promptStatusByType: {},
  histogram: null,
  matchState: null,
  versionCache: {},
  provenanceLog: {},
  colonySelection: { selectedTypeId: null, activeTab: 'flowchart', viewedVersion: null },
  mutationRejection: null,
  _repairCountsByType: {},
  matchResult: null,
  manifest: null,
  matchTimeline: initialMatchTimeline(),
  promptCycleCount: 0,
  replaySessionId: null,
  replayPlaying: false,
  replaySpeed: 1,
  replayPosition: 0,
  replayResult: null,
  hasSeenColonyIntro: false,
  guideStep: 0,
  guideDismissed: false,
  guideRequested: false,
};

/** The single client store (D11). Chrome reads it via the `useClientStore(selector)`
 * hook (subscribes, re-renders only on a selected-slice change); the Pixi render loop
 * reads `useClientStore.getState()` imperatively every frame — no React re-render on
 * that hot path. */
export const useClientStore = create<ClientStore>((set) => ({
  ...initialState,
  setSession: (session) => set(session),
  setLobby: (lobby) => set({ lobby }),
  setConnectionState: (connectionState) =>
    set((state) => ({
      connectionState,
      // streamEpoch (design D9/F7): bump once on ENTRY into `GapWait` (a sequence gap)
      // — never on recovery (`GapWait -> Keyframed`), so the counter stays monotonic and
      // is not derived from the transient, non-monotonic gap-recovery state (F7).
      streamEpoch:
        connectionState === ConnState.GapWait && state.connectionState !== ConnState.GapWait
          ? state.streamEpoch + 1
          : state.streamEpoch,
    })),
  setConnectionEpoch: (connectionEpoch) =>
    set((state) => ({
      connectionEpoch,
      // streamEpoch (design D9): a strictly-higher connection epoch is a reconnect
      // discontinuity — bump once (monotonic). `SessionController.onStateChange` calls
      // `setConnectionEpoch` on every transition with the current epoch, so a same-epoch
      // call never bumps.
      streamEpoch:
        connectionEpoch > state.connectionEpoch ? state.streamEpoch + 1 : state.streamEpoch,
    })),
  setConfirmedTier: (confirmedTier) => set({ confirmedTier }),
  setLastSeq: (lastSeq) => set({ lastSeq }),
  setTruncated: (truncated) => set({ truncated }),
  setWorld: (world) => set((state) => ({ previousWorld: state.currentWorld, currentWorld: world })),
  setWorldFrame: (world) =>
    set((state) => ({
      previousWorld: state.currentWorld,
      currentWorld: world,
      lastSeq: world.seq,
    })),
  reset: () => set(initialState),

  applyColonyMeta: (meta) =>
    set((state) => {
      const versionCache = { ...state.versionCache };
      for (const t of meta.types) {
        versionCache[versionCacheKey(t.typeId, t.activeVersion)] = t.canonicalText;
      }
      return { colonyMeta: meta, versionCache };
    }),

  applyPromptStatus: (status) =>
    set((state) => {
      const promptStatusByType = { ...state.promptStatusByType, [status.typeId]: status };
      const repairCounts = { ...state._repairCountsByType };
      // promptCycleCount (design D-sum-2): incremented once per observed
      // `generating` — never on `repairing`/`accepted`/`failed`/`stale` — so it
      // counts prompt *cycles*, distinct from `provenanceLog`'s accepted-version
      // count and from `promptStatusByType`'s latest-per-type snapshot.
      const promptCycleCount =
        status.status.kind === 'generating' ? state.promptCycleCount + 1 : state.promptCycleCount;
      switch (status.status.kind) {
        case 'generating':
          repairCounts[status.typeId] = 0;
          break;
        case 'repairing':
          repairCounts[status.typeId] = (repairCounts[status.typeId] ?? 0) + 1;
          break;
        case 'accepted': {
          const key = versionCacheKey(status.typeId, status.status.version);
          const existing = state.provenanceLog[key];
          // A reconnect can redeliver the exact same `accepted{v}` status (same
          // tick) with no accompanying `repairing` history this session — that is
          // a replay of an already-accreted event, not a fresh acceptance, so it
          // must not clobber a richer provenance entry already recorded with a
          // reset (0) repair count (client-colony "Reconnect's single ColonyMeta
          // rebinds without inventing history"). Only a genuinely new acceptance
          // (a different tick) overwrites/creates the entry.
          const isNewEvent = existing === undefined || existing.acceptedTick !== status.tick;
          const provenanceLog = isNewEvent
            ? {
                ...state.provenanceLog,
                [key]: { acceptedTick: status.tick, repairCount: repairCounts[status.typeId] ?? 0 },
              }
            : state.provenanceLog;
          repairCounts[status.typeId] = 0;
          return { promptStatusByType, provenanceLog, _repairCountsByType: repairCounts, promptCycleCount };
        }
        default:
          break;
      }
      return { promptStatusByType, _repairCountsByType: repairCounts, promptCycleCount };
    }),

  applyStateHistogram: (histogram) => set({ histogram }),
  applyMatchState: (matchState) =>
    set((state) => {
      // Role reconciliation, monotonic toward spectator (design DD7 / D-083): the own
      // colony (by `colonyId`) entering `eliminations` forces `role = 'spectator'` and
      // it never returns to 'player'. This makes `role` a faithful mirror of the
      // server's authorization `Role`, which flips to Spectator on elimination while
      // retaining the colony binding. Checked against the *incoming* `matchState`, not
      // the store's (still-previous) one.
      const ownEliminated =
        state.colonyId !== null && matchState.state.eliminations.includes(state.colonyId);
      return {
        matchState,
        role: ownEliminated ? 'spectator' : state.role,
        matchTimeline: accreteMatchTimeline(state.matchTimeline, matchState, state.connectionEpoch),
      };
    }),
  applyMutationRejected: (reason) => set({ mutationRejection: reason }),
  dismissMutationRejection: () => set({ mutationRejection: null }),

  selectColonyType: (typeId) =>
    set((state) => ({ colonySelection: { ...state.colonySelection, selectedTypeId: typeId } })),
  setColonyActiveTab: (tab) =>
    set((state) => ({ colonySelection: { ...state.colonySelection, activeTab: tab } })),
  setViewedVersion: (version) =>
    set((state) => ({ colonySelection: { ...state.colonySelection, viewedVersion: version } })),

  applyMatchResult: (result) => set({ matchResult: result }),
  setManifest: (manifest) => set({ manifest }),

  enterReplay: ({ replaySessionId, result }) =>
    set({
      replaySessionId,
      replayResult: result,
      replayPlaying: false,
      replaySpeed: 1,
      replayPosition: 0,
    }),
  setReplayPosition: (position) =>
    set((state) => {
      // Reaching the captured terminal tick applies the open-replay `MatchResult` to
      // `matchResult` exactly once (tasks 7.5/7.6), driving the existing
      // `matchResult`→Summary transition from the open-replay response — never a
      // streamed `ServerMessage::Result`. Applied once (guarded on the still-null
      // `matchResult`) so a further advance past terminal never re-applies/clobbers.
      const reachedTerminal = state.replayResult !== null && position >= state.replayResult.tick;
      const matchResult =
        reachedTerminal && state.matchResult === null ? state.replayResult : state.matchResult;
      return { replayPosition: position, matchResult };
    }),
  exitReplay: () =>
    set({
      replaySessionId: null,
      replayPlaying: false,
      replaySpeed: 1,
      replayPosition: 0,
      replayResult: null,
    }),

  setHasSeenColonyIntro: (hasSeenColonyIntro) => set({ hasSeenColonyIntro }),
  setGuideStep: (guideStep) => set({ guideStep }),
  resetGuideProgress: () => set({ guideStep: 0, guideDismissed: false, guideRequested: false }),
  requestGuide: () => set({ guideRequested: true, guideStep: 0, guideDismissed: false }),
}));

// --- colony slice selectors (design D12; own colony resolved by `SessionSlice.
// colonyId`, populated from `Welcome.yourColony`) -------------------------------

/** Resolves the own colony's `MatchState` scoreboard row by `SessionSlice.colonyId`
 * — the HUD's `livingCount`/`pool` source (D12). `null` until a `MatchState` has
 * arrived or `colonyId` is unset. */
export function selectOwnScoreboardRow(state: ClientStoreState): ScoreboardRow | null {
  if (state.matchState === null || state.colonyId === null) {
    return null;
  }
  return state.matchState.state.scoreboard.find((row) => row.colonyId === state.colonyId) ?? null;
}

/** Whether the own colony (by `SessionSlice.colonyId`) appears in the latest
 * `MatchState.eliminations` — the spectator/eliminated mutation-gating branch
 * (D9/D12). `false` until a `MatchState` has arrived or `colonyId` is unset. */
export function selectOwnEliminated(state: ClientStoreState): boolean {
  if (state.matchState === null || state.colonyId === null) {
    return false;
  }
  return state.matchState.state.eliminations.includes(state.colonyId);
}

/** Builds the additive, immutable render-input context (design D9) from the store's own
 * slices — the streamed `season` (`MatchState`), the own-hive world anchor, the own-colony
 * lifecycle (eliminated iff the own colony is in `MatchState.eliminations`), a live-play
 * null `playhead`, and the two monotonic discontinuity counters (`connectionEpoch` = the
 * Connection's reconnect epoch; `streamEpoch` = the explicit reconnect-or-gap counter).
 * None of these ride the `WorldSnapshot`: they arrive on other messages, independent of
 * world deltas. This is the seam the canvas call sites (`InMatchCanvas`/`Match`/`Colony`)
 * feed into `render()`; canvas internals never read the store directly. Before any
 * match/anchor state has arrived it equals `LIVE_PLAY_RENDER_INPUT`. The `replay-viewer`
 * lane is the consumer that will supply a non-null `playhead`. */
export function selectRenderInput(state: ClientStoreState): RenderInput {
  return {
    season: state.matchState?.state.season ?? LIVE_PLAY_RENDER_INPUT.season,
    ownHiveAnchor: state.ownHive,
    ownColonyLifecycle: selectOwnEliminated(state) ? 'eliminated' : 'live',
    ownColonyId: state.colonyId,
    playhead: LIVE_PLAY_RENDER_INPUT.playhead,
    connectionEpoch: state.connectionEpoch,
    streamEpoch: state.streamEpoch,
  };
}
